/**
 * Excel Entity Write Helper
 * Writes entity values to the current selection (single or multi-cell)
 */

/* global Excel */

import { runExcel } from "./runExcel";
import { logger } from "../shared/logger";

// Soft guardrail: maximum cells to prevent accidental huge fills
const MAX_CELLS = 10000;

/**
 * Pure helper: Create 2D array filled with values from factory
 * @param rows - Number of rows
 * @param cols - Number of columns
 * @param valueFactory - Function called rows * cols times to generate values
 * @returns 2D array of shape [rows][cols]
 */
export function fill2d<T>(rows: number, cols: number, valueFactory: () => T): T[][] {
  const result: T[][] = [];
  for (let r = 0; r < rows; r++) {
    const row: T[] = [];
    for (let c = 0; c < cols; c++) {
      row.push(valueFactory());
    }
    result.push(row);
  }
  return result;
}

/**
 * Write entity to current selection
 * Supports single-cell and multi-cell selections
 * @param entity - Excel EntityCellValue to write
 * @throws Error if selection is invalid or too large
 */
export async function writeEntityToSelection(entity: any): Promise<void> {
  try {
    await runExcel(async (context) => {
      // Get selected range
      const range = context.workbook.getSelectedRange();
      
      // Load range properties
      range.load(["rowCount", "columnCount"]);
      await context.sync();
      
      const rows = range.rowCount;
      const cols = range.columnCount;
      
      // Edge case: Check for huge selections (soft guardrail)
      const totalCells = rows * cols;
      if (totalCells > MAX_CELLS) {
        throw new Error("Selection is too large (max " + MAX_CELLS + " cells). Please select a smaller range.");
      }
      
      // Note: Office.js getSelectedRange() returns a single contiguous range.
      // Non-contiguous selections (multiple areas) are not directly supported by getSelectedRange().
      // If the user has a non-contiguous selection, getSelectedRange() will return the first area.
      // We could check for this by comparing selection.address with range.address, but it's complex.
      // For now, we'll proceed with the returned range. If needed, we can add detection later.
      
      // Build 2D array using fill2d() with factory that returns the same entity object (reuse, don't deep-clone)
      const array2d = fill2d(rows, cols, () => entity);
      
      // Assign to range using valuesAsJson (standard Office.js API for entity values)
      range.valuesAsJson = array2d;
      
      await context.sync();
      
      logger.log("writeEntityToSelection", "wrote entity to selection", "rows=" + rows + " cols=" + cols);
    });
  } catch (error) {
    logger.error("writeEntityToSelection", "write failed", error);
    throw error;
  }
}
